import cn from 'classnames';
import { debounce } from 'lodash';
import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';

import {
  ASIDE_MENU_WIDTH,
  ASIDE_WIDTH_SET_DELAY,
  BrowserStorage,
  Direction,
  NAV_ANIMATION_DELAY,
} from '@const/consts';
import { Path, Section } from '@const/paths';
import { ACTION_REQUIRED_COUNTS_UPDATED_EVENT } from '@const/events';

import BoxesService from '@services/boxes';
import EnvelopesService from '@services/envelopes';
import UsersService from '@services/users';
import UserFiltersService from '@services/userFilters';
import { IUserFilter } from '@services/userFilters/types';

import { useAppSelector, useAppDispatch } from '@hooks/redux';

import {
  Aside,
  Button,
  ILibNavItem,
  LibNavItemLevel,
  LibNavigationTheme,
  Navigation,
} from '@infologistics/frontend-libraries';
import MenuItem from './components/MenuItem';

import styles from './Menu.module.scss';

import { setMenuItems } from '@store/modules/navigation';
import {
  resetError403,
  resetError404,
  setPrevError,
} from '@store/modules/utils';

import { INavItem } from './types';
import { setUserSettings } from '@store/modules/user';
import { displayErrorNotification } from '@utils/utils';
import { ACTION_REQUIRED_ROUTE_FOLDER, addBoxMenuItems, getActionRequiredRoute } from './utils';
import {
  getUserFilterRoute,
  getUserFilterUrl,
  normalizeUserFilterParams,
  USER_FILTERS_UPDATED_EVENT,
} from '@utils/userFilters';

const Menu: FC = () => {
  const dispatch = useAppDispatch();
  const { activeMenu, menuItems } = useAppSelector((state) => state.navigation);
  const { isAuthenticated, profile, settings: userSettings } = useAppSelector((state) => state.user);
  const { boxes } = useAppSelector((state) => state.boxes);
  const currentBoxOguid = useAppSelector((state) => state.envelopes.currentBoxOguid);
  const { isError404, isError403, language } = useAppSelector(
    (state) => state.utils
  );
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const savedBoxOguidRef = useRef<string>('');
  const [newEnvelopeLoading, setNewEnvelopeLoading] = useState(false);
  const [userFilters, setUserFilters] = useState<IUserFilter[]>([]);
  const [actionRequiredCounts, setActionRequiredCounts] = useState<Record<string, number>>({});

  const normalizeMenuPath = (path: string): string =>
    path.split('?')[0].replace(/\/+$/, '');

  const isActionRequiredCountsRefreshRoute = (pathname: string): boolean => {
    const parts = normalizeMenuPath(pathname).split('/').filter(Boolean);
    const directionOrFolder = parts[2];

    return (
      parts[0] === 'envelopes' &&
      Boolean(parts[1]) &&
      [
        Direction.INCOMING,
        Direction.OUTGOING,
        ACTION_REQUIRED_ROUTE_FOLDER,
      ].includes(directionOrFolder ?? '')
    );
  };

  const normalizeMenuSearch = (path: string): string =>
    normalizeUserFilterParams(path.split('?')[1] ?? '');

  const getBoxOguidFromMenuPath = (path: string): string => {
    const [pathname] = path.split('?');
    const [, section, boxOguid] = pathname.split('/');
    const nonBoxRoutes = ['all', 'filters', Direction.INCOMING, Direction.OUTGOING];

    return section === 'envelopes' && boxOguid && !nonBoxRoutes.includes(boxOguid)
      ? boxOguid
      : '';
  };

  const isEnvelopeCardPath = (pathname: string): boolean => {
    const parts = normalizeMenuPath(pathname).split('/').filter(Boolean);

    return (
      (parts[0] === 'envelope' && Boolean(parts[1]) && parts[1] !== 'new') ||
      (parts[0] === 'envelopes' && parts[2] === 'envelope' && Boolean(parts[3]))
    );
  };

  const getLocationMenuPathname = (): string => {
    const locationMenuPathname = `${location.pathname}${location.search}`;

    if (!isEnvelopeCardPath(location.pathname)) {
      return locationMenuPathname;
    }

    return localStorage.getItem(BrowserStorage.LAST_URL) ?? locationMenuPathname;
  };

  const isMenuRouteMatch = (pathname: string, route: string): boolean => {
    const normalizedPathname = normalizeMenuPath(pathname);
    const normalizedRoute = normalizeMenuPath(route);
    const routeHasSearch = route.includes('?');

    if (routeHasSearch) {
      return (
        normalizedPathname === normalizedRoute &&
        normalizeMenuSearch(pathname) === normalizeMenuSearch(route)
      );
    }

    return (
      normalizedPathname === normalizedRoute ||
      normalizedPathname.startsWith(`${normalizedRoute}/`)
    );
  };

  const hasActiveMenuDescendant = (
    menuItem: INavItem,
    activeMenuPathname: string
  ): boolean =>
    Boolean(menuItem.items?.some((item) =>
      isMenuRouteMatch(activeMenuPathname, item.route) ||
      hasActiveMenuDescendant(item, activeMenuPathname)
    ));

  const getExtendedMenuItems = (
    menuItems: INavItem[],
    level = LibNavItemLevel.TOP,
    parentActiveMenuPathname?: string,
    parentOpenBoxOguid?: string,
    depth = 0
  ): ILibNavItem[] => {
    const locationMenuPathname = getLocationMenuPathname();
    const hasLocationMenuMatch = menuItems.some(({ route }) =>
      isMenuRouteMatch(locationMenuPathname, route)
    );
    const activeMenuPathname = parentActiveMenuPathname ?? (hasLocationMenuMatch
      ? locationMenuPathname
      : activeMenu || locationMenuPathname);
    const openBoxOguid = parentOpenBoxOguid ?? (
      getBoxOguidFromMenuPath(activeMenuPathname) ||
      currentBoxOguid ||
      profile.boxOguid
    );

    const activeMenuItems = menuItems
      .filter(({ route }) => isMenuRouteMatch(activeMenuPathname, route))
      .sort((a, b) => b.route.length - a.route.length);

    const activeMenuItemUuid = activeMenuItems.length
      ? activeMenuItems[0].uuid
      : '';

    return menuItems.map(
      (item): ILibNavItem =>
        getExtendedMenuItem(item, level, activeMenuItemUuid, activeMenuPathname, openBoxOguid, depth)
    );
  };

  const getExtendedMenuItem = (
    menuItem: INavItem,
    menuLevel: string,
    activeMenuItemUuid: string,
    activeMenuPathname: string,
    openBoxOguid: string,
    depth: number
  ): ILibNavItem => {
    const { hasPage = true, items, title, uuid } = menuItem;

    const isRouteActive = activeMenuItemUuid === uuid;
    const hasActiveDescendant = hasActiveMenuDescendant(menuItem, activeMenuPathname);
    const isActive = isRouteActive && hasPage && !hasActiveDescendant;
    const isTopLevel = menuLevel === LibNavItemLevel.TOP;
    const isSameOpenBox = Boolean(
      openBoxOguid && getBoxOguidFromMenuPath(menuItem.route) === openBoxOguid
    );
    const isOpenBoxRoot = depth === 0 && isSameOpenBox;
    const isOpen = Boolean(
      items && (isOpenBoxRoot || hasActiveDescendant || !isTopLevel)
    );

    const subMenuItemsForDropdown =
      items && getExtendedMenuItems(
        items,
        LibNavItemLevel.TOP,
        activeMenuPathname,
        openBoxOguid,
        depth + 1
      );

    return {
      ...menuItem,
      customNavLink: (
        <MenuItem
          key={uuid}
          item={menuItem}
          isActive={isActive}
          isOpen={isOpen}
        />
      ),
      isActive,
      isOpen,
      items: subMenuItemsForDropdown,
      level: menuLevel,
      title: t(title),
    };
  };

  const getNavItems = (): void => {
    const { boxOguid, email: profileEmail, fullName } = profile;
    const email = boxes.find((val) => val.isPersonal)?.email ?? profileEmail;
    const incomingRoute = getUserFilterRoute(boxOguid, Direction.INCOMING);
    const outgoingRoute = getUserFilterRoute(boxOguid, Direction.OUTGOING);
    const getUserFilterMenuItems = (route: string): INavItem[] =>
      userFilters
        .filter((filter) => filter.route === route)
        .map((filter) => ({
          hasPage: true,
          iconProps: { classes: 'mr-4' },
          isFavoriteFilter: true,
          route: getUserFilterUrl(filter),
          title: filter.title,
          uuid: filter.oguid,
        }));

    const items: INavItem[] = [
      {
        contentData: {
          content: email,
          heading: fullName,
        },
        hasPage: false,
        iconCollapse: 'IconAngle',
        iconCollapseProps: { classes: 'ml-auto icon-collapse', size: 'xs' },
        items: [
          {
            badge: {
              name: String(actionRequiredCounts[boxOguid] ?? 0),
              path: '',
              theme: 'primary',
            },
            icon: 'IconChecklist',
            iconProps: { classes: 'mr-2' },
            route: getActionRequiredRoute(boxOguid),
            title: 'envelopes:myTasks',
            uuid: uuidv4(),
          },
          {
            icon: 'IconIncomingAlt',
            iconProps: { classes: 'mr-2' },
            route: incomingRoute,
            title: 'envelopes:incoming',
            uuid: uuidv4(),
          },
          ...getUserFilterMenuItems(incomingRoute),
          {
            icon: 'IconOutgoingAlt',
            iconProps: { classes: 'mr-2' },
            route: outgoingRoute,
            title: 'envelopes:outgoing',
            uuid: uuidv4(),
          },
          ...getUserFilterMenuItems(outgoingRoute),
          {
            icon: 'IconUsers',
            iconProps: { classes: 'mr-2' },
            route: Path.ENVELOPES_BOX_CONTACT_DIRECTORY.replace(
              ':boxOguid',
              boxOguid
            ),
            title: 'boxes:settings.contactDirectory.menu',
            uuid: uuidv4(),
          },
          {
            hasPage: false,
            icon: 'IconCog',
            iconProps: { classes: 'mr-2' },
            route: Section.ENVELOPES_BOX_SETTINGS.replace(':boxOguid', boxOguid),
            title: 'boxes:settings.title',
            uuid: uuidv4(),
            items: [
              {
                iconProps: { classes: 'mr-4' },
                route: Path.ENVELOPES_BOX_SETTINGS.replace(':boxOguid', boxOguid),
                title: 'boxes:settings.general.menu',
                uuid: uuidv4(),
              },
              {
                iconProps: { classes: 'mr-4' },
                route: Path.ENVELOPES_BOX_USERS.replace(':boxOguid', boxOguid),
                title: 'boxes:users.title',
                uuid: uuidv4(),
              },
              {
                iconProps: { classes: 'mr-4' },
                route: Path.ENVELOPES_BOX_SUBDIVISIONS.replace(':boxOguid', boxOguid),
                title: 'boxes:settings.subdivisions.menu',
                uuid: uuidv4(),
              },
              {
                iconProps: { classes: 'mr-4' },
                route: Path.ENVELOPES_BOX_CUSTOM_FIELDS.replace(
                  ':boxOguid',
                  boxOguid
                ),
                title: 'boxes:settings.customFields.menu',
                uuid: uuidv4(),
              },
              {
                iconProps: { classes: 'mr-4' },
                route: Path.ENVELOPES_BOX_LABELS.replace(':boxOguid', boxOguid),
                title: 'boxes:settings.labels.menu',
                uuid: uuidv4(),
              },
              {
                iconProps: { classes: 'mr-4' },
                route: Path.ENVELOPES_BOX_ENVELOPE_TEMPLATES.replace(
                  ':boxOguid',
                  boxOguid
                ),
                title: 'boxes:settings.envelopeTemplates.menu',
                uuid: uuidv4(),
              },
            ],
          },
        ],
        route: Section.ENVELOPES_BOX_OGUID.replace(':boxOguid', boxOguid),
        title: fullName,
        uuid: uuidv4(),
      },
    ];

    const fullItems = addBoxMenuItems(items, boxes, userFilters, actionRequiredCounts);

    dispatch(setMenuItems(fullItems));
  };

  const loadUserFilters = (): void => {
    if (!isAuthenticated) return;

    UserFiltersService.getUserFilters()
      .then((response) => setUserFilters(response.data))
      .catch(displayErrorNotification);
  };

  const loadActionRequiredCounts = useCallback((): void => {
    if (!isAuthenticated) return;

    EnvelopesService.getActionRequiredCounts({ expandBoxes: true })
      .then((response) => {
        const counts = (response.data.boxes ?? []).reduce<Record<string, number>>((acc, boxCounts) => ({
          ...acc,
          [boxCounts.boxOguid]: boxCounts.actionRequiredTaskCount,
        }), {});

        setActionRequiredCounts(counts);
      })
      .catch(displayErrorNotification);
  }, [isAuthenticated]);

  const handleNewEnvelopeButtonClick = (): void => {
    if (newEnvelopeLoading) return;

    const focusedMenuPath = activeMenu || location.pathname;
    const focusedBoxOguid = focusedMenuPath.split('/')[2];
    const availableBoxOguids = [profile.boxOguid, ...boxes.map(({ oguid }) => oguid)];
    const boxOguid = availableBoxOguids.includes(focusedBoxOguid)
      ? focusedBoxOguid
      : currentBoxOguid || profile.boxOguid;

    if (!boxOguid) {
      navigate(Path.ENVELOPE_NEW);
      return;
    }

    setNewEnvelopeLoading(true);

    Promise.all([
      BoxesService.getEnvelopeTemplates(boxOguid),
      BoxesService.getEnvelopeFields(boxOguid),
    ])
      .then(([templatesResponse, fieldsResponse]) => {
        navigate(Path.ENVELOPE_NEW, {
          state: {
            activeMenu: focusedMenuPath,
            preloadedMetadata: {
              boxOguid,
              envelopeFields: fieldsResponse.data,
              envelopeTemplates: templatesResponse.data,
            },
          },
        });
      })
      .catch(displayErrorNotification)
      .finally(() => setNewEnvelopeLoading(false));
  };

  const handleResize = (asideWidth: string) => {
    dispatch(setUserSettings({ asideWidth }));

    UsersService.updateUserSettings({...userSettings, asideWidth }).catch(
      displayErrorNotification
    );
  };

  const handleWithDebounse = debounce(handleResize, ASIDE_WIDTH_SET_DELAY);

  useEffect(getNavItems, [language, boxes, userFilters, actionRequiredCounts]);

  useEffect(() => {
    loadUserFilters();
  }, [isAuthenticated]);

  useEffect(() => {
    loadActionRequiredCounts();
  }, [boxes, loadActionRequiredCounts, profile.boxOguid]);

  useEffect(() => {
    if (!isActionRequiredCountsRefreshRoute(location.pathname)) return;

    loadActionRequiredCounts();
  }, [loadActionRequiredCounts, location.pathname, location.search]);

  useEffect(() => {
    window.addEventListener(ACTION_REQUIRED_COUNTS_UPDATED_EVENT, loadActionRequiredCounts);

    return () => {
      window.removeEventListener(ACTION_REQUIRED_COUNTS_UPDATED_EVENT, loadActionRequiredCounts);
    };
  }, [loadActionRequiredCounts]);

  useEffect(() => {
    if (!isAuthenticated || !currentBoxOguid) return;
    if (savedBoxOguidRef.current === currentBoxOguid) return;
    if (userSettings.lastOpenedBoxOguid === currentBoxOguid) {
      savedBoxOguidRef.current = currentBoxOguid;
      return;
    }

    savedBoxOguidRef.current = currentBoxOguid;
    dispatch(setUserSettings({ lastOpenedBoxOguid: currentBoxOguid }));
    UsersService.updateUserSettings({
      ...userSettings,
      lastOpenedBoxOguid: currentBoxOguid,
    }).catch(displayErrorNotification);
  }, [currentBoxOguid, dispatch, isAuthenticated, userSettings]);

  useEffect(() => {
    window.addEventListener(USER_FILTERS_UPDATED_EVENT, loadUserFilters);

    return () => {
      window.removeEventListener(USER_FILTERS_UPDATED_EVENT, loadUserFilters);
    };
  }, [isAuthenticated]);
  useEffect(() => {
    if (!isAuthenticated) return;

    return () => {
      dispatch(setPrevError(isError403 || isError404));
      dispatch(resetError403());
      dispatch(resetError404());
    };
  }, [location.pathname]);

  return (
    <Aside onResize={handleWithDebounse} minWidth={ASIDE_MENU_WIDTH.MIN} maxWidth={ASIDE_MENU_WIDTH.MAX}>
      <div className='d-flex full-width'>
        <Button
          theme='warning'
          classes={cn('fw-700 my-2 mx-auto', styles.new_envelope_button)}
          isDisabled={newEnvelopeLoading}
          loading={newEnvelopeLoading}
          onClick={handleNewEnvelopeButtonClick}
        >
          {t('envelopes:new')}
        </Button>
      </div>
      <Navigation
        delay={NAV_ANIMATION_DELAY}
        items={getExtendedMenuItems(menuItems)}
        theme={LibNavigationTheme.ALTERNATIVE}
        height={'calc(100% - 48px)'}
      />
    </Aside>
  );
};

export default Menu;
